import { encodePeriodValue } from '@components'

import { createContext, ReactNode, useContext, useEffect, useMemo } from 'react'

import { useUserOrganization } from '@modules-deprecated/app/organization'

import { useGlobalFiscalYear } from '../../../../../../contexts/globalFiscalYearContext'
import { useLocalStorageUncontrolled } from '../../../../../../hooks/useLocalStorageUncontrolled'
import { useQueryParams } from '../../../../../../hooks/useQueryParams'
import { getPeriodValueFromFiscalYear } from '../../../../../../utils/getPeriodValueFromFiscalYear'
import { PeriodComparisonValue } from '../../../../../reports/types/periodComparisonValue'
import { ReportsRoute } from '../../../../enums/reportsRoute'
import { ReportOptions } from '../../../../types/reportOptions'
import { getLocalStorageFiltersValidated } from '../../../../utils/getLocalStorageFiltersValidated'
import { getReportsFiltersStorageKey } from '../../../../utils/getReportsFiltersStorageKey'

interface ProfitAndLossReportOptionsUrl {
  compareWith: PeriodComparisonValue
  hideZeroes?: string
  showChange?: string
  showGroupPercentage?: string
  compareCount?: string
  period?: string
}

export interface ProfitAndLossReportOptions extends Omit<ReportOptions, 'ytd'> {
  compareWith: PeriodComparisonValue
  showChange?: boolean
  showGroupPercentage?: boolean
}

interface ProfitAndLossFiltersContextState {
  appliedFilters: ProfitAndLossReportOptions
  setQueryParams: (
    filters:
      | Partial<ProfitAndLossReportOptionsUrl>
      | ((prevParams: Partial<ProfitAndLossReportOptionsUrl>) => Partial<ProfitAndLossReportOptionsUrl>),
  ) => void
}

const ProfitAndLossFiltersContext = createContext<ProfitAndLossFiltersContextState | undefined>(undefined)

const defaultReportsQuery: ProfitAndLossReportOptionsUrl = {
  compareCount: '1',
  compareWith: 'year',
  hideZeroes: 'true',
  period: undefined,
  showChange: undefined,
  showGroupPercentage: undefined,
}

interface ProfitAndLossFiltersContextProviderProps {
  children: ReactNode
}

export const ProfitAndLossFiltersContextProvider = ({ children }: ProfitAndLossFiltersContextProviderProps) => {
  const { organization } = useUserOrganization()
  const profitAndLossStorageKey = getReportsFiltersStorageKey(ReportsRoute.ProfitAndLoss, organization?.id)

  const { setItem, getItem } = useLocalStorageUncontrolled(profitAndLossStorageKey)

  const { fiscalYear: globalFiscalYear } = useGlobalFiscalYear()
  const defaultPeriodValue =
    globalFiscalYear && organization?.id
      ? encodePeriodValue(getPeriodValueFromFiscalYear(globalFiscalYear), organization.id)
      : undefined

  const localStorageFilters = getLocalStorageFiltersValidated(defaultReportsQuery, getItem())

  const [queryParams, setQueryParams] = useQueryParams<ProfitAndLossReportOptionsUrl>({
    defaultQuery: {
      ...defaultReportsQuery,
      ...(defaultPeriodValue ? { period: defaultPeriodValue } : {}),
      ...localStorageFilters,
    },
  })

  useEffect(() => {
    const localStorageFiltersString = JSON.stringify(localStorageFilters)
    const queryParamsString = JSON.stringify(queryParams)

    if (organization?.id && localStorageFiltersString !== queryParamsString) {
      setItem(queryParamsString)
    }
  }, [localStorageFilters, organization?.id, queryParams, setItem])

  const appliedFilters = useMemo(() => {
    const { hideZeroes, showChange, showGroupPercentage, compareCount } = queryParams

    return {
      ...queryParams,
      hideZeroes: hideZeroes === 'true',
      showChange: showChange === 'true',
      showGroupPercentage: showGroupPercentage === 'true',
      compareCount: compareCount ? parseInt(compareCount, 10) : undefined,
    }
  }, [queryParams])

  return (
    <ProfitAndLossFiltersContext.Provider
      value={{
        appliedFilters,
        setQueryParams,
      }}
    >
      {children}
    </ProfitAndLossFiltersContext.Provider>
  )
}

export const useProfitAndLossFilters = () => {
  const context = useContext(ProfitAndLossFiltersContext)

  if (!context) {
    throw new Error('ProfitAndLossFiltersContext is missing in the module!')
  }

  return context
}
